/* Performance Analytics — bespoke interactive dashboards.
 * These three pages have behaviour the generic data-driven renderer can't
 * express: the At-Risk order-gap heatmap, and the rep/client picker scorecards.
 * Logic ported 1:1 from the design mock-up. */
import { useMemo, useState } from 'react';
import { KpiRow, ChartPanel, DataTable } from './parts';
import {
  CLIENTS, CLIENT_NAMES, TESTS, REPS, MONTHS, PALETTE as C, series, one, pick, seedRand,
} from './mockData';
import type { Cell, Kpi } from './types';

/* ════════════════════ At-Risk / Inactive Clients ════════════════════ */
const ATRISK_WEEKS = 26;

function buildClientOrderHistory(client: string, seed: number) {
  const cadenceMap: Record<string, number> = { AQT: 2, BAT: 3, COA: 1, DWO: 1, HHD: 2, JEL: 4, RRA: 2, WCO: 3 };
  const cadence = cadenceMap[client] ?? 2;
  const r = seedRand(seed);
  const weeks = Array<boolean>(ATRISK_WEEKS).fill(false);
  let nextOrderIn = Math.round(r() * cadence) + 1;
  for (let w = 0; w < ATRISK_WEEKS; w++) {
    nextOrderIn--;
    if (nextOrderIn <= 0) { weeks[w] = true; nextOrderIn = Math.max(1, Math.round(cadence + (r() - 0.5) * cadence * 0.6)); }
  }
  return { cadence, weeks };
}

function analyzeClientGap(client: string, seed: number) {
  const history = buildClientOrderHistory(client, seed);
  const gapClients: Record<string, number> = { HHD: 7, JEL: 9 };  // forced "went quiet" demo pattern
  if (gapClients[client]) for (let w = ATRISK_WEEKS - gapClients[client]; w < ATRISK_WEEKS; w++) history.weeks[w] = false;

  const cadenceDays = history.cadence * 7;
  let lastOrderWeek = -1;
  for (let w = ATRISK_WEEKS - 1; w >= 0; w--) if (history.weeks[w]) { lastOrderWeek = w; break; }
  const weeksSinceLast = lastOrderWeek === -1 ? ATRISK_WEEKS : ATRISK_WEEKS - 1 - lastOrderWeek;
  const daysSinceLast = weeksSinceLast * 7 + Math.round(seedRand(seed + 1)() * 6);
  return { client, history, cadenceDays, daysSinceLast, daysOverdue: daysSinceLast - cadenceDays };
}

export function AtRiskPage() {
  const { analyses, kpis, rows } = useMemo(() => {
    const a = CLIENTS.map((c, i) => analyzeClientGap(c, 1000 + i * 37)).sort((x, y) => y.daysOverdue - x.daysOverdue);
    const flagged = a.filter((x) => x.daysOverdue > 7);
    const totalAtRisk = flagged.reduce((s, x) => s + Math.round(40000 + one(40000, 30000, 2000 + CLIENTS.indexOf(x.client as never))), 0);
    const worst = a[0]!;
    const avgOverdue = Math.round(flagged.reduce((s, x) => s + x.daysOverdue, 0) / Math.max(1, flagged.length));
    const ks: Kpi[] = [
      { label: 'Clients With a Gap', value: String(flagged.length), delta: `— of ${CLIENTS.length} active clients`, dir: 'down' },
      { label: 'Revenue at Risk', value: `$${Math.round(totalAtRisk / 1000)}K`, delta: 'annualized, flagged clients', dir: 'down' },
      { label: 'Worst Gap', value: worst.client, delta: `${worst.daysOverdue}d overdue`, dir: 'down' },
      { label: 'Avg Days Overdue', value: String(avgOverdue), delta: 'flagged clients only', dir: 'flat' },
    ];
    const tableRows: Cell[][] = a.map((x) => {
      const annualVal = Math.round(40000 + one(40000, 30000, 2000 + CLIENTS.indexOf(x.client as never)));
      const cadenceLabel = x.cadenceDays <= 7 ? 'Weekly' : x.cadenceDays <= 14 ? 'Every 2 weeks' : x.cadenceDays <= 21 ? 'Every 3 weeks' : 'Monthly+';
      const status = x.daysOverdue > 21 ? 'Critical' : x.daysOverdue > 7 ? 'Flagged' : 'On schedule';
      const pill = status === 'Critical' ? 'red' : status === 'Flagged' ? 'amber' : 'green';
      return [
        `${CLIENT_NAMES[x.client]} (${x.client})`, cadenceLabel, `${x.daysSinceLast} days ago`,
        x.daysOverdue > 0 ? `+${x.daysOverdue}d` : `${x.daysOverdue}d`, `$${annualVal.toLocaleString()}`,
        { text: status, pill } as Cell,
      ];
    });
    return { analyses: a, kpis: ks, rows: tableRows };
  }, []);

  const axis = Array.from({ length: ATRISK_WEEKS }, (_, w) =>
    (ATRISK_WEEKS - w - 1) === 0 ? 'now' : (w % 4 === 0 ? `-${ATRISK_WEEKS - w - 1}w` : ''));

  return (
    <>
      <div className="pa-page-title">At-Risk / Inactive Clients <span className="pa-badge ai">AI</span></div>
      <div className="pa-page-desc">Detects clients who have gone quiet relative to their normal ordering rhythm — built to catch a sudden stop, not just low volume.</div>
      <KpiRow kpis={kpis} />

      <div className="pa-panel">
        <div className="pa-panel-title">26-Week Order Activity Heatmap</div>
        <div className="pa-panel-sub">Each cell is one week. Green = sample received that week. Red = a gap beyond that client's normal cadence. Grey = a quiet week that's still within their normal rhythm.</div>
        <div className="pa-heatmap-wrap">
          <div className="pa-heatmap-axis">{axis.map((l, i) => <span key={i}>{l}</span>)}</div>
          <div>
            {analyses.map((a) => {
              const flagged = a.daysOverdue > 7;
              return (
                <div className="pa-heatmap-row" key={a.client}>
                  <div className="pa-heatmap-label">{a.client}{flagged && <span className="pa-gap-flag">GAP</span>}</div>
                  <div className="pa-heatmap-cells">
                    {a.history.weeks.map((hasOrder, w) => {
                      const inGap = !hasOrder && a.daysOverdue > 0 && w >= ATRISK_WEEKS - Math.ceil(a.daysSinceLast / 7);
                      const cls = hasOrder ? 'has-order' : inGap ? 'gap' : '';
                      const tip = hasOrder ? `${CLIENT_NAMES[a.client]}: order received`
                        : inGap ? `${CLIENT_NAMES[a.client]}: no order — overdue` : `${CLIENT_NAMES[a.client]}: no order this week`;
                      return <div className={`pa-heatmap-cell ${cls}`} data-tip={tip} key={w} />;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="pa-heatmap-legend">
          <div className="pa-legend-item"><span className="pa-legend-swatch" style={{ background: C.green }} />Order received</div>
          <div className="pa-legend-item"><span className="pa-legend-swatch" style={{ background: '#ededed' }} />Quiet, within normal rhythm</div>
          <div className="pa-legend-item"><span className="pa-legend-swatch" style={{ background: '#f5dede', border: '1px solid #e8b8b8' }} />Gap — overdue vs. their pattern</div>
        </div>
        <div className="pa-chart-note"><b>How to read this:</b> Scan each row left to right. A solid run of green is a healthy, active client. A block of red picking up partway through a row is the signature of a client who used to order regularly and then suddenly stopped — that's the pattern this page is built to surface.</div>
      </div>

      <div className="pa-panel">
        <div className="pa-panel-title">Clients Ranked by Days Overdue</div>
        <div className="pa-panel-sub">Sorted by how far each client is past their own expected reorder date — so a quarterly client and a weekly client are judged fairly against their own history.</div>
        <DataTable table={{ cols: ['Client', 'Normal Cadence', 'Last Order', 'Days Overdue', 'Annual Value', 'Status'], rows }} />
        <div className="pa-chart-note"><b>How to read this:</b> "Days Overdue" is days since the last order minus how long this client normally waits between orders. Zero or negative means they're on schedule. The largest positive numbers are the clients most worth a phone call today.</div>
      </div>
    </>
  );
}

/* ════════════════════ Salesperson Scorecard ════════════════════ */
interface RepProfile { initials: string; quotaTTM: number; segment: string; tenure: string; clients: string[]; }
const REP_PROFILES: Record<string, RepProfile> = {
  'Chris D.': { initials: 'CD', quotaTTM: 750000, segment: 'Municipal Water & Industrial', tenure: '6 yrs', clients: ['DWO', 'COA', 'HHD'] },
  'Wilson T.': { initials: 'WT', quotaTTM: 700000, segment: 'Environmental Consulting', tenure: '4 yrs', clients: ['AQT', 'BAT', 'RRA'] },
  'Selvi R.': { initials: 'SR', quotaTTM: 650000, segment: 'Private Utility & New Business', tenure: '3 yrs', clients: ['JEL', 'WCO'] },
};

export function RepScorecardPage() {
  const [activeRep, setActiveRep] = useState('Chris D.');
  const view = useMemo(() => {
    const seed = 800 + REPS.indexOf(activeRep) * 50;
    const p = REP_PROFILES[activeRep]!;
    const actual = series(12, p.quotaTTM / 12, (p.quotaTTM / 12) * 0.25, seed);
    const ttmActual = actual.reduce((a, b) => a + b, 0);
    const attainment = Math.round((ttmActual / p.quotaTTM) * 100);
    const winRate = Math.round(28 + one(12, 8, seed + 1));
    const dealStages = ['Lead', 'Qualified', 'Quote Sent', 'Negotiation', 'Won', 'Lost'];
    const dealRows: Cell[][] = Array.from({ length: 8 }, (_, i) => {
      const client = pick(p.clients.concat([...CLIENTS]), seed + 40 + i);
      const stage = pick(dealStages, seed + 41 + i);
      const value = Math.round(15000 + one(40000, 30000, seed + 42 + i));
      const days = Math.round(one(15, 12, seed + 43 + i));
      const status = stage === 'Won' ? 'Closed Won' : stage === 'Lost' ? 'Closed Lost' : 'Open';
      const pill = status === 'Closed Won' ? 'green' : status === 'Closed Lost' ? 'red' : 'amber';
      return [CLIENT_NAMES[client] ?? client, stage, `$${value.toLocaleString()}`, `${days} days ago`, { text: status, pill } as Cell];
    });
    return { seed, p, actual, ttmActual, attainment, winRate, dealRows };
  }, [activeRep]);

  const { seed, p, actual, ttmActual, attainment, winRate, dealRows } = view;
  const attClass = attainment >= 100 ? 'green' : attainment >= 85 ? 'amber' : 'red';

  return (
    <>
      <div className="pa-page-title">Salesperson Scorecard</div>
      <div className="pa-page-desc">Full performance profile for an individual rep — revenue, quota, pipeline, win rate, and client portfolio.</div>

      <div className="pa-picker-tabs">
        {REPS.map((r) => (
          <button key={r} className={`pa-picker-tab${r === activeRep ? ' active' : ''}`} onClick={() => setActiveRep(r)}>{r}</button>
        ))}
      </div>

      <div className="pa-profile-header">
        <div className="pa-profile-avatar">{p.initials}</div>
        <div className="pa-profile-info">
          <h3>{activeRep}</h3>
          <div className="pa-profile-sub">{p.segment} &nbsp;•&nbsp; {p.tenure} at BTSOFT &nbsp;•&nbsp; Manages {p.clients.length} client accounts</div>
        </div>
        <div className="pa-profile-badges">
          <span className={`pa-profile-badge ${attClass}`}>{attainment}% of quota</span>
          <span className="pa-profile-badge blue">{winRate}% win rate</span>
        </div>
      </div>

      <KpiRow kpis={[
        { label: 'TTM Revenue', value: `$${(ttmActual / 1000).toFixed(0)}K`, delta: `${attainment}% of $${(p.quotaTTM / 1000).toFixed(0)}K quota`, dir: attainment >= 100 ? 'up' : 'down' },
        { label: 'Win Rate', value: `${winRate}%`, delta: 'trailing 12mo', dir: 'flat' },
        { label: 'Open Pipeline', value: `$${Math.round(80 + one(60, 40, seed + 2))}K`, delta: `${Math.round(4 + one(4, 3, seed + 3))} active deals`, dir: 'flat' },
        { label: 'Avg Deal Size', value: `$${Math.round(28 + one(15, 10, seed + 4))}K`, delta: '▲ vs team avg', dir: 'up' },
      ]} />

      <div className="pa-panel-row pa-cols-2e">
        <ChartPanel panel={{
          title: 'Revenue vs Quota — 12 months', tall: true,
          chart: { type: 'line', labels: MONTHS, legend: true, series: [
            { name: 'Actual', color: C.orange, data: actual },
            { name: 'Quota', color: C.grey, dashed: true, data: Array(12).fill(Math.round(p.quotaTTM / 12)) },
          ] },
          note: "The orange line is this rep's monthly closed revenue; the dashed grey line is their monthly quota. Orange consistently above grey means they're running ahead of target.",
        }} />
        <ChartPanel panel={{
          title: 'Pipeline by Stage', tall: true,
          chart: { type: 'bar', horizontal: true, labels: ['Lead', 'Qualified', 'Quote Sent', 'Negotiation', 'Won'],
            data: [0, 1, 2, 3, 4].map((i) => one(Math.max(0, 8 - i * 1.3), 3, seed + 10 + i)),
            color: [C.grey, C.blueLight, C.blue, C.orange, C.green] },
          note: "Bar length shows how many of this rep's open deals sit at each stage. Most deals stuck early suggests prospecting is strong but closing needs attention, or vice versa.",
        }} />
      </div>

      <div className="pa-panel-row pa-cols-2e">
        <ChartPanel panel={{
          title: 'Win Rate Trend — 12 months', tall: true,
          chart: { type: 'line', labels: MONTHS, legend: true, yMin: 0, yMax: 60, series: [
            { name: activeRep, color: C.orange, data: series(12, winRate, 8, seed + 20) },
            { name: 'Team Avg', color: C.grey, dashed: true, data: series(12, 36, 4, 999) },
          ] },
          note: "This line tracks the rep's personal win rate by month. Compare against the team average line to see if they're over- or under-performing peers.",
        }} />
        <ChartPanel panel={{
          title: 'Revenue by Client', tall: true,
          chart: { type: 'bar', horizontal: true, labels: p.clients, data: p.clients.map((_, i) => one(180000, 90000, seed + 30 + i)), color: C.green },
          note: 'Bar length is trailing-12-month revenue this rep has generated from each client they manage. Longer bars are their strongest accounts.',
        }} />
      </div>

      <div className="pa-panel">
        <div className="pa-panel-title">Deal Activity Log</div>
        <div className="pa-panel-sub">Most recent opportunities for this rep</div>
        <DataTable table={{ cols: ['Client', 'Stage', 'Value', 'Last Activity', 'Status'], rows: dealRows }} />
      </div>
    </>
  );
}

/* ════════════════════ Client Contribution ════════════════════ */
interface ClientProfile { rep: string; since: number; segment: string; }
const CLIENT_PROFILES: Record<string, ClientProfile> = {
  AQT: { rep: 'Wilson T.', since: 2019, segment: 'Industrial' },
  BAT: { rep: 'Wilson T.', since: 2021, segment: 'Municipal Water' },
  COA: { rep: 'Chris D.', since: 2018, segment: 'Municipal Water' },
  DWO: { rep: 'Chris D.', since: 2017, segment: 'Municipal Water' },
  HHD: { rep: 'Chris D.', since: 2020, segment: 'Private Utility' },
  JEL: { rep: 'Selvi R.', since: 2025, segment: 'Environmental Consulting' },
  RRA: { rep: 'Wilson T.', since: 2022, segment: 'Municipal Water' },
  WCO: { rep: 'Selvi R.', since: 2023, segment: 'Private Utility' },
};

export function ClientContributionPage() {
  const [activeClient, setActiveClient] = useState('DWO');
  const view = useMemo(() => {
    const idx = CLIENTS.indexOf(activeClient as never);
    const seed = 900 + idx * 50;
    const p = CLIENT_PROFILES[activeClient]!;
    const monthlyRev = series(12, 17000, 7000, seed);
    const ttmRev = monthlyRev.reduce((a, b) => a + b, 0);
    const sharePct = +((ttmRev / 2100000) * 100).toFixed(1);
    const tenureYrs = 2026 - p.since;
    const orderRows: Cell[][] = Array.from({ length: 8 }, (_, i) => {
      const test = pick(TESTS, seed + 20 + i);
      const value = Math.round(800 + one(1500, 1200, seed + 21 + i));
      const days = Math.round(one(15, 12, seed + 22 + i));
      const status = pick(['Billed', 'Pending', 'Billed', 'Billed'], seed + 23 + i);
      return [`WO-${4000 + idx * 100 + i}`, `${days} days ago`, test, `$${value.toLocaleString()}`,
        { text: status, pill: status === 'Billed' ? 'green' : 'amber' } as Cell];
    });
    return { idx, seed, p, monthlyRev, ttmRev, sharePct, tenureYrs, orderRows };
  }, [activeClient]);

  const { seed, p, monthlyRev, ttmRev, sharePct, tenureYrs, orderRows } = view;

  return (
    <>
      <div className="pa-page-title">Client Contribution</div>
      <div className="pa-page-desc">Detailed sales contribution breakdown for an individual client — revenue, trend, test mix, and account health.</div>

      <div className="pa-picker-tabs">
        {CLIENTS.map((c) => (
          <button key={c} className={`pa-picker-tab${c === activeClient ? ' active' : ''}`} onClick={() => setActiveClient(c)}>{c}</button>
        ))}
      </div>

      <div className="pa-profile-header">
        <div className="pa-profile-avatar client">{activeClient}</div>
        <div className="pa-profile-info">
          <h3>{CLIENT_NAMES[activeClient]}</h3>
          <div className="pa-profile-sub">{p.segment} &nbsp;•&nbsp; Client since {p.since} ({tenureYrs} yrs) &nbsp;•&nbsp; Account rep: {p.rep}</div>
        </div>
        <div className="pa-profile-badges">
          <span className={`pa-profile-badge ${sharePct > 15 ? 'amber' : 'blue'}`}>{sharePct}% of total revenue</span>
        </div>
      </div>

      <KpiRow kpis={[
        { label: 'TTM Revenue', value: `$${Math.round(ttmRev / 1000)}K`, delta: '▲ vs prior TTM', dir: 'up' },
        { label: '% of Company Revenue', value: `${sharePct}%`, delta: '— concentration', dir: 'flat' },
        { label: 'Avg Order Value', value: `$${Math.round(1000 + one(800, 500, seed + 5))}`, delta: '— stable', dir: 'flat' },
        { label: 'Account Rep', value: p.rep, delta: `since ${p.since}`, dir: 'flat' },
      ]} />

      <div className="pa-panel-row pa-cols-2e">
        <ChartPanel panel={{
          title: 'Monthly Revenue — 12 months', tall: true,
          chart: { type: 'bar', labels: MONTHS, data: monthlyRev, color: C.orange },
          note: "Each bar is this client's billed revenue that month. A steady or rising pattern is a healthy account; a declining trend is an early churn signal.",
        }} />
        <ChartPanel panel={{
          title: 'Test Mix Contribution', tall: true,
          chart: { type: 'pie', legendPos: 'right', labels: TESTS.slice(0, 6), data: [28, 22, 18, 14, 10, 8],
            colors: [C.orange, C.blue, C.green, C.amber, C.red, C.grey] },
          note: "Each slice shows what share of this client's spend comes from each test category. A heavily concentrated client carries more risk if that program ends.",
        }} />
      </div>

      <div className="pa-panel-row pa-cols-2e">
        <ChartPanel panel={{
          title: 'Revenue Share of Total Company Sales', tall: true,
          chart: { type: 'pie', donut: true, legendPos: 'bottom', labels: [activeClient, 'All Other Clients'],
            data: [sharePct, +(100 - sharePct).toFixed(1)], colors: [C.orange, C.grey] },
          note: 'The highlighted slice shows what percentage of all company revenue comes from this one client. Larger slices mean more revenue concentration risk if that client were lost.',
        }} />
        <ChartPanel panel={{
          title: 'Sample Volume — 12 months', tall: true,
          chart: { type: 'line', labels: MONTHS, series: [{ color: C.blue, fill: true, data: series(12, 180, 60, seed + 15) }] },
          note: 'This line tracks how many samples the client submitted each month. Volume and revenue should move together — if revenue is flat while volume drops, average order value is rising.',
        }} />
      </div>

      <div className="pa-panel">
        <div className="pa-panel-title">Order History</div>
        <div className="pa-panel-sub">Most recent billed work orders for this client</div>
        <DataTable table={{ cols: ['Order ID', 'Date', 'Test(s)', 'Value', 'Status'], rows: orderRows }} />
      </div>
    </>
  );
}
